import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Package,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit,
  Trash2,
  ChevronRight,
  Shield,
  AlertTriangle,
  Calendar,
  Building2,
  Wrench,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Loader2,
  FileText,
  Settings,
  X,
  QrCode,
  Download,
  Printer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Asset type definitions - Healthcare Equipment Categories
const ASSET_TYPES = [
  // Diagnostic & Imaging
  { id: "imaging", label: "Imaging (MRI/CT/X-Ray)", icon: "🔬", category: "Diagnostic" },
  { id: "ultrasound", label: "Ultrasound", icon: "📡", category: "Diagnostic" },
  { id: "lab_equipment", label: "Lab Equipment", icon: "🧪", category: "Diagnostic" },
  
  // Life Support & Critical Care
  { id: "ventilator", label: "Ventilator", icon: "💨", category: "Critical Care" },
  { id: "patient_monitor", label: "Patient Monitor", icon: "📊", category: "Critical Care" },
  { id: "defibrillator", label: "Defibrillator/AED", icon: "⚡", category: "Critical Care" },
  { id: "infusion_pump", label: "Infusion Pump", icon: "💉", category: "Critical Care" },
  
  // Sterilization & Safety
  { id: "sterilization", label: "Sterilization/Autoclave", icon: "🔬", category: "Safety" },
  { id: "atp_device", label: "ATP Testing Device", icon: "✅", category: "Safety" },
  { id: "water_purification", label: "Water Purification", icon: "💧", category: "Safety" },
  
  // Building & Infrastructure
  { id: "hvac", label: "HVAC System", icon: "❄️", category: "Infrastructure" },
  { id: "plumbing", label: "Plumbing System", icon: "🔧", category: "Infrastructure" },
  { id: "electrical", label: "Electrical System", icon: "⚡", category: "Infrastructure" },
  { id: "generator", label: "Generator/Backup Power", icon: "🔋", category: "Infrastructure" },
  { id: "elevator", label: "Elevator/Lift", icon: "🛗", category: "Infrastructure" },
  { id: "fire_safety", label: "Fire Safety System", icon: "🔥", category: "Infrastructure" },
  
  // Medical Equipment
  { id: "surgical", label: "Surgical Equipment", icon: "🏥", category: "Medical" },
  { id: "beds_stretchers", label: "Beds & Stretchers", icon: "🛏️", category: "Medical" },
  { id: "wheelchairs", label: "Wheelchairs/Mobility", icon: "♿", category: "Medical" },
  
  // IT & Communications
  { id: "nurse_call", label: "Nurse Call System", icon: "🔔", category: "IT" },
  { id: "medical_it", label: "Medical IT/EMR Devices", icon: "💻", category: "IT" },
  
  // Other
  { id: "other", label: "Other Equipment", icon: "📦", category: "Other" }
];

// Group asset types by category for dropdown organization
const ASSET_CATEGORIES = [...new Set(ASSET_TYPES.map(t => t.category))];


export default function AssetRegistry() {
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [partsDue, setPartsDue] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [activeTab, setActiveTab] = useState("assets");
  
  // Modal states
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showAssetDetails, setShowAssetDetails] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAddPart, setShowAddPart] = useState(false);
  const [showAddWarranty, setShowAddWarranty] = useState(false);
  
  // Form states
  const [assetForm, setAssetForm] = useState({
    name: "",
    asset_type: "",
    serial_number: "",
    model_number: "",
    manufacturer: "",
    hospital_id: "",
    hospital_name: "",
    location_details: "",
    install_date: ""
  });
  
  const [partForm, setPartForm] = useState({
    part_name: "",
    part_number: "",
    manufacturer: "",
    replacement_interval_months: 12,
    estimated_cost: 0,
    notes: ""
  });
  
  const [warrantyForm, setWarrantyForm] = useState({
    warranty_type: "manufacturer",
    provider: "",
    start_date: "",
    end_date: "",
    coverage_details: "",
    terms: ""
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [hospitals, setHospitals] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("sterilemate_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [assetsRes, partsDueRes, hospitalsRes] = await Promise.all([
        axios.get(`${API}/assets`, { headers }),
        axios.get(`${API}/assets/parts-due`, { headers }),
        axios.get(`${API}/hospitals`, { headers }).catch(() => ({ data: { hospitals: [] } }))
      ]);
      
      setAssets(assetsRes.data.assets || []);
      setPartsDue(partsDueRes.data.parts_due || []);
      setHospitals(hospitalsRes.data.hospitals || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    if (!assetForm.name || !assetForm.asset_type) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("sterilemate_token");
      await axios.post(`${API}/assets`, assetForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Asset created successfully");
      setShowAddAsset(false);
      resetAssetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create asset");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPart = async (e) => {
    e.preventDefault();
    if (!partForm.part_name || !selectedAsset) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("sterilemate_token");
      await axios.post(`${API}/assets/${selectedAsset.id}/parts`, partForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Part added successfully");
      setShowAddPart(false);
      resetPartForm();
      
      // Refresh asset details
      const res = await axios.get(`${API}/assets/${selectedAsset.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAsset(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add part");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddWarranty = async (e) => {
    e.preventDefault();
    if (!warrantyForm.provider || !warrantyForm.end_date || !selectedAsset) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("sterilemate_token");
      await axios.post(`${API}/assets/${selectedAsset.id}/warranties`, warrantyForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Warranty added successfully");
      setShowAddWarranty(false);
      resetWarrantyForm();
      
      // Refresh asset details
      const res = await axios.get(`${API}/assets/${selectedAsset.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAsset(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add warranty");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordReplacement = async (assetId, partId) => {
    try {
      const token = localStorage.getItem("sterilemate_token");
      await axios.put(`${API}/assets/${assetId}/parts/${partId}/replace`, {
        notes: "Scheduled replacement"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Replacement recorded");
      fetchData();
      
      if (selectedAsset?.id === assetId) {
        const res = await axios.get(`${API}/assets/${assetId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedAsset(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record replacement");
    }
  };

  const viewAssetDetails = async (asset) => {
    try {
      const token = localStorage.getItem("sterilemate_token");
      const res = await axios.get(`${API}/assets/${asset.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAsset(res.data);
      setShowAssetDetails(true);
    } catch (error) {
      toast.error("Failed to load asset details");
    }
  };

  const resetAssetForm = () => {
    setAssetForm({
      name: "",
      asset_type: "",
      serial_number: "",
      model_number: "",
      manufacturer: "",
      hospital_id: "",
      hospital_name: "",
      location_details: "",
      install_date: ""
    });
  };

  const resetPartForm = () => {
    setPartForm({
      part_name: "",
      part_number: "",
      manufacturer: "",
      replacement_interval_months: 12,
      estimated_cost: 0,
      notes: ""
    });
  };

  const resetWarrantyForm = () => {
    setWarrantyForm({
      warranty_type: "manufacturer",
      provider: "",
      start_date: "",
      end_date: "",
      coverage_details: "",
      terms: ""
    });
  };

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.hospital_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || asset.asset_type === filterType;
    return matchesSearch && matchesType;
  });

  const getPriorityBadge = (priority) => {
    const styles = {
      critical: "bg-red-100 text-red-700 border-red-200",
      high: "bg-orange-100 text-orange-700 border-orange-200",
      medium: "bg-amber-100 text-amber-700 border-amber-200"
    };
    return styles[priority] || styles.medium;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" data-testid="asset-registry-title">Asset Registry</h2>
          <p className="text-slate-500 mt-1">Manage equipment, warranties, and replacement schedules</p>
        </div>
        <Button onClick={() => setShowAddAsset(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-asset-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Asset
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{assets.length}</p>
              <p className="text-sm text-slate-500">Total Assets</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{partsDue.length}</p>
              <p className="text-sm text-slate-500">Parts Due</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {assets.filter(a => a.warranties?.some(w => new Date(w.end_date) > new Date())).length}
              </p>
              <p className="text-sm text-slate-500">Under Warranty</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {[...new Set(assets.map(a => a.hospital_id).filter(Boolean))].length}
              </p>
              <p className="text-sm text-slate-500">Facilities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="assets" data-testid="assets-tab">
            <Package className="w-4 h-4 mr-2" />
            All Assets
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="alerts-tab">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Alerts ({partsDue.length})
          </TabsTrigger>
        </TabsList>

        {/* Assets Tab */}
        <TabsContent value="assets" className="mt-4">
          {/* Filters */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-assets"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48" data-testid="filter-type">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ASSET_TYPES.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.icon} {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} data-testid="refresh-assets">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Assets Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Asset</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Warranty</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Parts Due</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAssets.map(asset => {
                  const hasActiveWarranty = asset.warranties?.some(w => new Date(w.end_date) > new Date());
                  const partsDueCount = asset.parts?.filter(p => {
                    const nextDate = new Date(p.next_replacement_date);
                    return nextDate <= new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
                  }).length || 0;
                  
                  return (
                    <tr key={asset.id} className="hover:bg-slate-50" data-testid={`asset-row-${asset.id}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{asset.name}</p>
                          <p className="text-sm text-slate-500">{asset.serial_number || "No S/N"}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium text-slate-700">
                          {ASSET_TYPES.find(t => t.id === asset.asset_type)?.label || asset.asset_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-slate-900">{asset.hospital_name || "—"}</p>
                          <p className="text-xs text-slate-500">{asset.location_details || "—"}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {hasActiveWarranty ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-sm">
                            <Shield className="w-4 h-4" />
                            Active
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {partsDueCount > 0 ? (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                            {partsDueCount} due
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewAssetDetails(asset)}
                          data-testid={`view-asset-${asset.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredAssets.length === 0 && (
              <div className="py-12 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No assets found</p>
                <Button onClick={() => setShowAddAsset(true)} className="mt-4" variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Asset
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Parts Due for Replacement</h3>
              <p className="text-sm text-slate-500">Parts due within the next 60 days</p>
            </div>
            
            <div className="divide-y divide-slate-100">
              {partsDue.map((item, idx) => (
                <div key={idx} className="px-4 py-4 flex items-center justify-between hover:bg-slate-50" data-testid={`alert-${idx}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      item.priority === "critical" ? "bg-red-100" :
                      item.priority === "high" ? "bg-orange-100" : "bg-amber-100"
                    }`}>
                      <AlertTriangle className={`w-5 h-5 ${
                        item.priority === "critical" ? "text-red-600" :
                        item.priority === "high" ? "text-orange-600" : "text-amber-600"
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{item.part_name}</p>
                      <p className="text-sm text-slate-500">
                        {item.asset_name} • {item.hospital_name || "Unknown Location"}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityBadge(item.priority)}`}>
                        {item.priority?.toUpperCase()}
                      </span>
                      <p className="text-sm text-slate-500 mt-1">
                        Due: {new Date(item.next_replacement_date).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleRecordReplacement(item.asset_id, item.part_id)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`record-replacement-${idx}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Record
                    </Button>
                  </div>
                </div>
              ))}
              
              {partsDue.length === 0 && (
                <div className="py-12 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                  <p className="text-slate-500">No parts due for replacement</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Asset Modal */}
      <Dialog open={showAddAsset} onOpenChange={setShowAddAsset}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Add New Asset
            </DialogTitle>
            <DialogDescription>
              Register a new piece of equipment in the asset registry
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateAsset} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Asset Name *</Label>
                <Input
                  value={assetForm.name}
                  onChange={(e) => setAssetForm({...assetForm, name: e.target.value})}
                  placeholder="e.g., RO Water System Unit 1"
                  className="mt-1"
                  data-testid="asset-name-input"
                />
              </div>
              
              <div>
                <Label>Asset Type *</Label>
                <Select
                  value={assetForm.asset_type}
                  onValueChange={(v) => setAssetForm({...assetForm, asset_type: v})}
                >
                  <SelectTrigger className="mt-1" data-testid="asset-type-select">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_TYPES.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Serial Number</Label>
                <Input
                  value={assetForm.serial_number}
                  onChange={(e) => setAssetForm({...assetForm, serial_number: e.target.value})}
                  placeholder="ABC123456"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Manufacturer</Label>
                <Input
                  value={assetForm.manufacturer}
                  onChange={(e) => setAssetForm({...assetForm, manufacturer: e.target.value})}
                  placeholder="Manufacturer name"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Model Number</Label>
                <Input
                  value={assetForm.model_number}
                  onChange={(e) => setAssetForm({...assetForm, model_number: e.target.value})}
                  placeholder="Model #"
                  className="mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Hospital/Facility</Label>
                <Input
                  value={assetForm.hospital_name}
                  onChange={(e) => setAssetForm({...assetForm, hospital_name: e.target.value})}
                  placeholder="Hospital name"
                  className="mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Location Details</Label>
                <Input
                  value={assetForm.location_details}
                  onChange={(e) => setAssetForm({...assetForm, location_details: e.target.value})}
                  placeholder="Building A, Floor 2, Room 204"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Install Date</Label>
                <Input
                  type="date"
                  value={assetForm.install_date}
                  onChange={(e) => setAssetForm({...assetForm, install_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddAsset(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="create-asset-btn">
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating...</>
                ) : (
                  "Create Asset"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Asset Details Modal */}
      <Dialog open={showAssetDetails} onOpenChange={setShowAssetDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              {selectedAsset?.name}
            </DialogTitle>
            <DialogDescription>
              {ASSET_TYPES.find(t => t.id === selectedAsset?.asset_type)?.label} • {selectedAsset?.serial_number || "No S/N"}
            </DialogDescription>
          </DialogHeader>

          {selectedAsset && (
            <div className="space-y-6">
              {/* Asset Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Manufacturer</p>
                  <p className="font-medium text-slate-900">{selectedAsset.manufacturer || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Model</p>
                  <p className="font-medium text-slate-900">{selectedAsset.model_number || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-medium text-slate-900">{selectedAsset.hospital_name || "—"}</p>
                  <p className="text-sm text-slate-500">{selectedAsset.location_details || ""}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Install Date</p>
                  <p className="font-medium text-slate-900">
                    {selectedAsset.install_date ? new Date(selectedAsset.install_date).toLocaleDateString() : "—"}
                  </p>
                </div>
              </div>

              {/* QR Code Section */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
                      <QrCode className="w-4 h-4 text-blue-600" />
                      Asset QR Code
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Scan this code to view asset details or create a maintenance request
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          const svg = document.getElementById(`qr-${selectedAsset.id}`);
                          const svgData = new XMLSerializer().serializeToString(svg);
                          const canvas = document.createElement("canvas");
                          const ctx = canvas.getContext("2d");
                          const img = new Image();
                          img.onload = () => {
                            canvas.width = img.width;
                            canvas.height = img.height;
                            ctx.fillStyle = "white";
                            ctx.fillRect(0, 0, canvas.width, canvas.height);
                            ctx.drawImage(img, 0, 0);
                            const pngFile = canvas.toDataURL("image/png");
                            const downloadLink = document.createElement("a");
                            downloadLink.download = `asset-${selectedAsset.id}-qr.png`;
                            downloadLink.href = pngFile;
                            downloadLink.click();
                          };
                          img.src = "data:image/svg+xml;base64," + btoa(svgData);
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          const printWindow = window.open('', '_blank');
                          printWindow.document.write(`
                            <html>
                              <head><title>Asset QR - ${selectedAsset.name}</title></head>
                              <body style="text-align:center;padding:40px;font-family:system-ui">
                                <h2>${selectedAsset.name}</h2>
                                <p style="color:#666">${selectedAsset.serial_number || 'No S/N'} | ${selectedAsset.hospital_name}</p>
                                ${document.getElementById(`qr-${selectedAsset.id}`).outerHTML}
                                <p style="margin-top:20px;font-size:12px;color:#999">Asset ID: ${selectedAsset.id}</p>
                              </body>
                            </html>
                          `);
                          printWindow.document.close();
                          printWindow.print();
                        }}
                      >
                        <Printer className="w-4 h-4 mr-1" />
                        Print Label
                      </Button>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <QRCodeSVG 
                      id={`qr-${selectedAsset.id}`}
                      value={`${window.location.origin}/asset/${selectedAsset.id}`}
                      size={120}
                      level="M"
                      includeMargin={true}
                    />
                  </div>
                </div>
              </div>

              {/* Warranties Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-600" />
                    Warranties ({selectedAsset.warranties?.length || 0})
                  </h4>
                  <Button size="sm" variant="outline" onClick={() => setShowAddWarranty(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                
                {selectedAsset.warranties?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedAsset.warranties.map((warranty, idx) => {
                      const isActive = new Date(warranty.end_date) > new Date();
                      return (
                        <div key={idx} className={`p-3 rounded-lg border ${isActive ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{warranty.provider}</p>
                              <p className="text-sm text-slate-500">{warranty.warranty_type} warranty</p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                {isActive ? "Active" : "Expired"}
                              </span>
                              <p className="text-xs text-slate-500 mt-1">
                                Expires: {new Date(warranty.end_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">No warranties registered</p>
                )}
              </div>

              {/* Parts Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-blue-600" />
                    Parts ({selectedAsset.parts?.length || 0})
                  </h4>
                  <Button size="sm" variant="outline" onClick={() => setShowAddPart(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                
                {selectedAsset.parts?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedAsset.parts.map((part, idx) => {
                      const nextDate = new Date(part.next_replacement_date);
                      const isDue = nextDate <= new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
                      const isOverdue = nextDate < new Date();
                      
                      return (
                        <div key={idx} className={`p-3 rounded-lg border ${isOverdue ? "bg-red-50 border-red-200" : isDue ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{part.part_name}</p>
                              <p className="text-sm text-slate-500">
                                {part.part_number ? `#${part.part_number}` : ""} • Every {part.replacement_interval_months} months
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <p className={`text-sm font-medium ${isOverdue ? "text-red-600" : isDue ? "text-amber-600" : "text-slate-600"}`}>
                                  {isOverdue ? "Overdue" : isDue ? "Due Soon" : "OK"}
                                </p>
                                <p className="text-xs text-slate-500">
                                  Next: {nextDate.toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant={isDue ? "default" : "outline"}
                                className={isDue ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                                onClick={() => handleRecordReplacement(selectedAsset.id, part.id)}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">No parts registered</p>
                )}
              </div>

              {/* Service History Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-purple-600" />
                    Service History ({selectedAsset.service_events?.length || 0})
                  </h4>
                </div>
                
                {selectedAsset.service_events?.length > 0 ? (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {[...selectedAsset.service_events].reverse().map((event, idx) => (
                      <div key={idx} className="p-3 rounded-lg border bg-slate-50 border-slate-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{event.event_type}</p>
                            <p className="text-sm text-slate-600">{event.description}</p>
                            {event.contractor_name && (
                              <p className="text-xs text-slate-500 mt-1">By: {event.contractor_name}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">
                              {new Date(event.date).toLocaleDateString()}
                            </p>
                            {event.cost && (
                              <p className="text-sm font-medium text-slate-700">${event.cost.toLocaleString()}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">No service history recorded</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Part Modal */}
      <Dialog open={showAddPart} onOpenChange={setShowAddPart}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Part</DialogTitle>
            <DialogDescription>Add a replaceable part to this asset</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddPart} className="space-y-4">
            <div>
              <Label>Part Name *</Label>
              <Input
                value={partForm.part_name}
                onChange={(e) => setPartForm({...partForm, part_name: e.target.value})}
                placeholder="e.g., RO Membrane Filter"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Part Number</Label>
                <Input
                  value={partForm.part_number}
                  onChange={(e) => setPartForm({...partForm, part_number: e.target.value})}
                  placeholder="Part #"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Replacement Interval (months)</Label>
                <Input
                  type="number"
                  value={partForm.replacement_interval_months}
                  onChange={(e) => setPartForm({...partForm, replacement_interval_months: parseInt(e.target.value) || 12})}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Manufacturer</Label>
              <Input
                value={partForm.manufacturer}
                onChange={(e) => setPartForm({...partForm, manufacturer: e.target.value})}
                placeholder="Part manufacturer"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Estimated Cost ($)</Label>
              <Input
                type="number"
                value={partForm.estimated_cost}
                onChange={(e) => setPartForm({...partForm, estimated_cost: parseFloat(e.target.value) || 0})}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={partForm.notes}
                onChange={(e) => setPartForm({...partForm, notes: e.target.value})}
                placeholder="Additional notes..."
                className="mt-1"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddPart(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Part"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Warranty Modal */}
      <Dialog open={showAddWarranty} onOpenChange={setShowAddWarranty}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Warranty</DialogTitle>
            <DialogDescription>Register warranty coverage for this asset</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddWarranty} className="space-y-4">
            <div>
              <Label>Warranty Type</Label>
              <Select
                value={warrantyForm.warranty_type}
                onValueChange={(v) => setWarrantyForm({...warrantyForm, warranty_type: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manufacturer">Manufacturer</SelectItem>
                  <SelectItem value="extended">Extended</SelectItem>
                  <SelectItem value="service_contract">Service Contract</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Provider *</Label>
              <Input
                value={warrantyForm.provider}
                onChange={(e) => setWarrantyForm({...warrantyForm, provider: e.target.value})}
                placeholder="Warranty provider name"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={warrantyForm.start_date}
                  onChange={(e) => setWarrantyForm({...warrantyForm, start_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={warrantyForm.end_date}
                  onChange={(e) => setWarrantyForm({...warrantyForm, end_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Coverage Details</Label>
              <Textarea
                value={warrantyForm.coverage_details}
                onChange={(e) => setWarrantyForm({...warrantyForm, coverage_details: e.target.value})}
                placeholder="What's covered..."
                className="mt-1"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddWarranty(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Warranty"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
